import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import {
  CheckCircle2,
  ChevronRight,
  MessageSquareText,
  Search,
  Send
} from 'lucide-react';

import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';

const STATUS_LABELS = {
  sent: 'Nouvelle',
  in_review: 'En cours',
  waiting_teacher: 'En attente de l’enseignant',
  resolved: 'Traitée'
};

const STATUS_OPTIONS = [
  { value: 'sent', label: 'Nouvelle' },
  { value: 'in_review', label: 'En cours' },
  { value: 'waiting_teacher', label: 'En attente de l’enseignant' },
  { value: 'resolved', label: 'Traitée' }
];

function displayDate(value, withTime = false) {
  if (!value) return '—';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('fr-FR', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' }).format(date);
}

function sortTime(item) {
  return item?.lastMessageAt?.toMillis?.() || item?.updatedAt?.toMillis?.() || item?.createdAt?.toMillis?.() || 0;
}

export default function TeacherRequestsAdmin() {
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const adminName = profile?.fullname || profile?.fullName || profile?.displayName || 'Administration';

  useEffect(() => {
    return onSnapshot(collection(db, 'teacherRequests'), (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => sortTime(b) - sortTime(a));
      setRequests(rows);
      setLoading(false);
    }, (snapshotError) => {
      console.error('Demandes du personnel :', snapshotError);
      setError('Impossible de charger les demandes du personnel.');
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    return onSnapshot(collection(db, 'teacherRequests', selectedId, 'messages'), (snapshot) => {
      setMessages(snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0)));
    }, (snapshotError) => {
      console.error('Conversation demande du personnel :', snapshotError);
      setError('Impossible de charger cette conversation.');
    });
  }, [selectedId]);

  const selected = useMemo(
    () => requests.find((item) => item.id === selectedId) || null,
    [requests, selectedId]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return requests.filter((item) => {
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'open' && item.status !== 'resolved')
        || item.status === statusFilter;
      if (!matchesStatus) return false;
      if (!needle) return true;
      return [item.teacherName, item.subject, item.category]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [requests, search, statusFilter]);

  async function sendReply(event) {
    event.preventDefault();
    if (!selected || !reply.trim()) return;
    setReplying(true);
    setError('');
    setNotice('');
    try {
      const requestRef = doc(db, 'teacherRequests', selected.id);
      const messageRef = doc(collection(db, 'teacherRequests', selected.id, 'messages'));
      const batch = writeBatch(db);
      const now = Timestamp.now();
      const nextStatus = selected.status === 'sent' ? 'in_review' : selected.status;

      batch.set(messageRef, {
        authorType: 'administration',
        authorUid: user.uid,
        authorName: adminName,
        text: reply.trim(),
        createdAt: serverTimestamp()
      });

      const update = {
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'administration'
      };
      if (nextStatus !== selected.status) {
        update.status = nextStatus;
        update.statusHistory = [
          ...(Array.isArray(selected.statusHistory) ? selected.statusHistory : []),
          { status: nextStatus, at: now, byType: 'administration', byUid: user.uid }
        ];
      }
      batch.update(requestRef, update);
      await batch.commit();
      setReply('');
      setNotice('Réponse envoyée.');
    } catch (replyError) {
      console.error('Réponse administration :', replyError);
      setError('Impossible d’envoyer la réponse.');
    } finally {
      setReplying(false);
    }
  }

  async function changeStatus(nextStatus) {
    if (!selected || nextStatus === selected.status) return;
    setStatusSubmitting(true);
    setError('');
    setNotice('');
    try {
      await updateDoc(doc(db, 'teacherRequests', selected.id), {
        status: nextStatus,
        statusHistory: [
          ...(Array.isArray(selected.statusHistory) ? selected.statusHistory : []),
          { status: nextStatus, at: Timestamp.now(), byType: 'administration', byUid: user.uid }
        ],
        updatedAt: serverTimestamp()
      });
      setNotice('Statut mis à jour.');
    } catch (statusError) {
      console.error('Statut demande personnel :', statusError);
      setError('Impossible de modifier le statut.');
    } finally {
      setStatusSubmitting(false);
    }
  }

  return (
    <section className="teacher-admin-requests-v1">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="success-note"><CheckCircle2 size={18} />{notice}</div>}

      <div className="teacher-admin-request-toolbar-v1">
        <label className="teacher-admin-request-search-v1"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un enseignant ou une demande…" /></label>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="open">Demandes ouvertes</option>
          <option value="all">Toutes les demandes</option>
          {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      <div className="teacher-admin-request-layout-v1">
        <article className="panel teacher-admin-request-list-v1">
          <div className="personnel-section-heading personnel-list-heading">
            <span className="personnel-section-icon"><MessageSquareText size={22} /></span>
            <div><p className="eyebrow">PERSONNEL</p><h2>Demandes reçues</h2><p className="muted">{loading ? 'Chargement…' : `${filtered.length} demande${filtered.length > 1 ? 's' : ''}`}</p></div>
          </div>

          {!loading && filtered.map((item) => (
            <button key={item.id} type="button" className={`teacher-admin-request-item-v1 ${selectedId === item.id ? 'active' : ''}`} onClick={() => { setSelectedId(item.id); setNotice(''); setError(''); }}>
              <div className="teacher-admin-request-avatar-v1">{String(item.teacherName || 'E').charAt(0).toUpperCase()}</div>
              <div>
                <div><strong>{item.teacherName || 'Enseignant'}</strong><span className={`complaint-status-v3 status-${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span></div>
                <b>{item.subject}</b>
                <small>{item.category} · {displayDate(item.lastMessageAt || item.createdAt, true)}</small>
              </div>
              <ChevronRight size={17} />
            </button>
          ))}

          {!loading && !filtered.length && <div className="compact-empty">Aucune demande ne correspond à ce filtre.</div>}
        </article>

        <article className="panel teacher-admin-request-detail-v1">
          {!selected ? (
            <div className="complaint-empty-v3">
              <MessageSquareText size={30} />
              <strong>Sélectionnez une demande</strong>
              <span>La conversation avec l’enseignant apparaîtra ici.</span>
            </div>
          ) : (
            <>
              <div className="teacher-admin-request-detail-head-v1">
                <div><p className="eyebrow">{selected.category}</p><h2>{selected.subject}</h2><p>{selected.teacherName}{selected.concernedDate ? ` · Date concernée : ${displayDate(selected.concernedDate)}` : ''}</p></div>
                <label>Statut
                  <select value={selected.status} disabled={statusSubmitting} onChange={(event) => changeStatus(event.target.value)}>
                    {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>

              <div className="teacher-admin-thread-v1">
                {messages.map((message) => (
                  <article key={message.id} className={`complaint-message-v2 ${message.authorType === 'administration' ? 'from-admin' : 'from-parent'}`}>
                    <strong>{message.authorType === 'administration' ? message.authorName || 'Administration' : selected.teacherName || 'Enseignant'}</strong>
                    <p>{message.text}</p>
                    <small>{displayDate(message.createdAt, true)}</small>
                  </article>
                ))}
                {!messages.length && <div className="compact-empty">Aucun message.</div>}
              </div>

              {selected.status !== 'resolved' && (
                <form className="complaint-reply-composer-v3" onSubmit={sendReply}>
                  <label>Répondre à l’enseignant</label>
                  <textarea value={reply} onChange={(event) => setReply(event.target.value)} maxLength={5000} placeholder="Écrivez votre réponse…" required />
                  <div><span>Vous pouvez ensuite placer la demande en attente de l’enseignant ou la marquer comme traitée.</span><button className="button button-primary" type="submit" disabled={replying || !reply.trim()}><Send size={16} />{replying ? 'Envoi…' : 'Envoyer'}</button></div>
                </form>
              )}
            </>
          )}
        </article>
      </div>
    </section>
  );
}
